import React, { useCallback, useContext, useRef, useState } from "react";
import { numberFormatter } from "@common/helpers/number";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { usePaginationWithBulkReset } from "@components/transactions/hooks/usePaginationWithBulkReset";
import { HStack } from "@design-system/HStack/HStack";
import { TransactionsTotals } from "../TransactionsTotals";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "../PaginatedTransactionsTable.context";
import { useTransactionBulkActions } from "../TransactionsBulkActions/useTransactionBulkActions";
import { TransactionBulkModalsFromActions } from "../TransactionsBulkActions/TransactionBulkModalsFromActions";
import { useFooterPassingStatistics } from "./useFooterPassingStatistics";

const MAX_WIDTH_FOR_HIDING_ACTIONS_HOTKEYS = 900;
const MAX_WIDTH_FOR_HIDING_NUMBER_OF_TOTAL_TO_SELECT = 900;

export interface TransactionsViewFooterProps {
    actionComponent?: () => React.ReactNode;
    scrollTo?: (options: ScrollToOptions) => void;
}

export const TransactionsViewFooter = ({
    actionComponent,
    scrollTo,
}: TransactionsViewFooterProps) => {
    const { currentPage, totalPages, transactions } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setPage } = useContext(PaginatedTransactionsActionsContext);

    const onPageChange = (newPage: number) => {
        setPage(newPage);
        scrollTo?.({ top: 0, behavior: "smooth" });
    };

    const txBulk = useTransactionBulkActions({
        isEnabled: !actionComponent,
    });
    const passingStatistics = useFooterPassingStatistics();

    const [commandIslandWidth, setCommandIslandWidth] = useState(0);
    const observerRef = useRef<ResizeObserver | null>(null);

    const islandRef = useCallback((node: HTMLDivElement | null) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }

        if (!node) {
            return;
        }

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setCommandIslandWidth(entry.contentRect.width);
            }
        });

        resizeObserver.observe(node);
        observerRef.current = resizeObserver;
    }, []);

    const pageChangeWithBulkActionReset = usePaginationWithBulkReset({
        onPageChange,
    });

    if (!transactions || transactions.length === 0) {
        return null;
    }

    const isSelected = txBulk.selectedCount > 0 && !actionComponent;
    const hasPagination = totalPages > 1;
    const isBusy = txBulk.isBusy;

    const hideActionsHotkeys =
        commandIslandWidth < MAX_WIDTH_FOR_HIDING_ACTIONS_HOTKEYS;
    const hideNumberOfTotalToSelect =
        commandIslandWidth < MAX_WIDTH_FOR_HIDING_NUMBER_OF_TOTAL_TO_SELECT;

    return (
        <>
            <CommandIsland
                sticky
                ref={islandRef}
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <HStack gap={8}>
                    {hasPagination && (
                        <Pagination
                            pageCount={totalPages}
                            currentPage={currentPage}
                            disabled={isBusy}
                            onPageChange={pageChangeWithBulkActionReset}
                            size="md"
                            buttonsPosition="left"
                        />
                    )}

                    {passingStatistics && (
                        <TransactionsTotals {...passingStatistics} />
                    )}

                    {isSelected && (
                        <>
                            <Tooltip
                                key={txBulk.selectedCount}
                                content={`Deselect ${txBulk.selectedCount}`}
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={txBulk.clearSelection}
                                >
                                    {txBulk.selectedCount} selected
                                </Button>
                            </Tooltip>
                            {txBulk.showSelectAll && (
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={txBulk.selectAll}
                                    data-testid="bulk-select-all-button"
                                >
                                    Select all
                                    {!hideNumberOfTotalToSelect && (
                                        <span>
                                            {" "}
                                            {numberFormatter.format(
                                                txBulk.totalTransactions,
                                            )}
                                        </span>
                                    )}
                                </Button>
                            )}
                        </>
                    )}
                </HStack>

                {isSelected ? (
                    <Tooltip content="Open bulk actions">
                        <Button
                            variant="primary"
                            size="md"
                            onClick={txBulk.handleOpenBulkActions}
                            disabled={txBulk.isBusy}
                            rightIcon={
                                hideActionsHotkeys ? undefined : (
                                    <span data-slot-box="">
                                        <Shortcut keys="mod+k" />
                                    </span>
                                )
                            }
                        >
                            Actions
                        </Button>
                    </Tooltip>
                ) : (
                    <>{actionComponent ? actionComponent() : null}</>
                )}
            </CommandIsland>

            <TransactionBulkModalsFromActions bulkActions={txBulk} />
        </>
    );
};
